import { Button, Checkbox, FormControlLabel, Chip, Avatar, Tooltip } from "@mui/material";
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { MdOutlineMotionPhotosPaused } from "react-icons/md";
import { useSelector } from 'react-redux';
import { UserAPI } from "../../../api/UserAPI";
import { SubscriptionAPI } from "../../../api/SubscriptionAPI";
import defaultProfileImg from '../../../../src/assets/images/default_profile_img.png';
import freeTierImg from '../../../../src/assets/images/free-trial.png';
import { GrInProgress } from "react-icons/gr";
import { Privileges } from "../../../util/PrivilegeConstants";
import { useDispatch } from "react-redux";
import { PrivilegeAPI } from "../../../api/PrivilegesAPI";
import { rootAction } from "../../../redux/reducer/rootReducer";
import CustomDialog from '../../CustomComponents/CustomDialog/CustomDialog.jsx';
import { getPlayerLabelByGame } from "../../../util/SideBarUtil.jsx";

// ✅ Custom Toolbar with built-in quick filter
const CustomToolbar = ({ quickFilterProps }) => {
    return (
        <div style={{ 
            width: '100%',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            padding: '8px 16px',
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                alignItems: 'center',
            }}>
                <GridToolbarQuickFilter
                    {...quickFilterProps}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: '8px',
                            height: '38px',
                            backgroundColor: 'white',
                            width: '320px',
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '14px',
                            padding: '0 12px',
                        },
                    }}
                    placeholder="Search by Name, Email, or Player ID..."
                    debounceMs={400}
                    quickFilterParser={(searchInput) => 
                        searchInput.split(',').map((value) => value.trim())
                    }
                />
            </div>
        </div>
    );
};

const PlayerList = () => {
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const dispatch = useDispatch();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [loadingState, setLoadingState] = useState({
        loading1: false,
        loading2: false,
        loading3: false
    })
    const [users, setUsers] = useState([]);
    const [cancelSubInfo, setCancelSubInfo] = useState(null);
    const [refresh, setRefresh] = useState('');
    const [isRefundAll, setIsRefundAll] = useState(false);
    const [privileges, setPrivileges] = useState({});
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [filterData, setFilterData] = useState({});
    const [filterModel, setFilterModel] = useState({ items: [] });
    
    // ✅ Track sorting state with default sort by playerName
    const [sortModel, setSortModel] = useState([
        { field: 'playerName', sort: 'asc' }
    ]);

    const startLoading = (key) => {
        setLoadingState(prev => ({
            ...prev, [key]: true
        }))
    };
    const finishLoading = (key) => {
        setLoadingState(prev => ({
            ...prev, [key]: false
        }))
    };

    const findAllPrivileges = async () => {
        try {
            startLoading("loading1")
            const reqObj = {
                userId: userInfo.userId,
                featureName: Object.keys(Privileges)[7]
            }
            const response = await PrivilegeAPI.getAllPrivilegesByFeature(reqObj);
            setPrivileges(response.Data);
        } catch (error) {
            console.error(error);
        } finally {
            finishLoading("loading1")
        }
    };

    // ✅ Handle API call with filter and sorting
    const fetchMemberAndPlayers = async (filterText = '', size, pageNum, sortData = null) => {
        try {
            const finalSize = size !== undefined ? size : paginationModel.pageSize;
            const finalPage = pageNum !== undefined ? pageNum : paginationModel.page;
            
            // ✅ Build sort params from sortModel
            let sortParams = {};
            if (sortData) {
                sortParams = sortData;
            } else if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'playerName': 'playerName',
                    'roleName': 'roleName',
                    'playerStatus': 'playerStatus',
                    'email': 'email',
                    'subStatus': 'subStatus',
                    'planName': 'planName'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            console.log("🔍 Calling API with:", { 
                page: finalPage, 
                size: finalSize, 
                filter: filterText,
                sort: sortParams 
            });
            
            startLoading("loading2")
            const res = await UserAPI.getAllMemberAndPlayer(
                selectedClub.clubId,
                finalSize,
                finalPage,
                filterText || '',
                filterData,
                sortParams  // ✅ Pass sort params
            )
            setUsers(res.Data || []);
        } catch (error) {
            console.error(error);
        } finally {
            finishLoading("loading2")
        }
    }

    // ✅ Handle sort model changes
    const handleSortModelChange = (newSortModel) => {
        setSortModel(newSortModel);
        const filterValue = filterModel?.quickFilterValues?.join(',') || '';
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // Build sort params
        let sortParams = {};
        if (newSortModel && newSortModel.length > 0) {
            const sort = newSortModel[0];
            const fieldMap = {
                'playerName': 'playerName',
                'roleName': 'roleName',
                'playerStatus': 'playerStatus',
                'email': 'email',
                'subStatus': 'subStatus',
                'planName': 'planName'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchMemberAndPlayers(filterValue, paginationModel.pageSize, 0, sortParams);
    };

    // ✅ Handle filter changes from DataGrid
    const handleFilterChange = (newFilterModel) => {
        setFilterModel(newFilterModel);
        
        const quickFilterValue = newFilterModel?.quickFilterValues?.join(',') || '';
        
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // Preserve current sort when filtering
        let sortParams = {};
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            const fieldMap = {
                'playerName': 'playerName',
                'roleName': 'roleName',
                'playerStatus': 'playerStatus',
                'email': 'email',
                'subStatus': 'subStatus',
                'planName': 'planName'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchMemberAndPlayers(quickFilterValue, paginationModel.pageSize, 0, sortParams);
    };

    useEffect(() => {
        if (userInfo.userId) {
            findAllPrivileges();
        }
    }, [userInfo.userId])

    // ✅ Initial load and pagination changes with sorting
    useEffect(() => {
        if (selectedClub?.clubId) {
            const filterValue = filterModel?.quickFilterValues?.join(',') || '';
            
            let sortParams = {};
            if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'playerName': 'playerName',
                    'roleName': 'roleName',
                    'playerStatus': 'playerStatus',
                    'email': 'email',
                    'subStatus': 'subStatus',
                    'planName': 'planName'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            fetchMemberAndPlayers(filterValue, paginationModel.pageSize, paginationModel.page, sortParams);
        }
    }, [selectedClub?.clubId, paginationModel.page, paginationModel.pageSize, refresh]);
    
    const cancelSubscription = (subscriptionId, userId, userName) => {
        setOpenDeleteModal(true);
        setCancelSubInfo({
            userId: userId,
            clubId: selectedClub.clubId,
            subscriptionId: subscriptionId,
            subscriptionCancelReason: "N/A",
            userName: userName
        })
    }
    
    const cancelSubscriptionApi = async () => {
        try {
            setOpenDeleteModal(false);
            startLoading("loading3")
            const result = await SubscriptionAPI.deleteSubscription(cancelSubInfo, isRefundAll)
            setRefresh(true);
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'success', content: cancelSubInfo.userName + ' subscription is canceled successfully' }));
        } catch (error) {
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: 'Something went wrong' }));
        } finally {
            finishLoading("loading3")
        }
    }

    // ✅ Updated columns with proper widths, sorting, and filter disabled
    const columns = useMemo(() => [
        {
            field: 'profileImage',
            headerName: 'Profile',
            flex: 0.5,
            minWidth: 70,
            maxWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.row.playerName || ''} arrow>
                    <Avatar 
                        src={params.value ? `${import.meta.env.VITE_APP_BASE_URL}/user/user-profile-img/${params.value}` : defaultProfileImg}
                        sx={{ width: 40, height: 40 }}
                    />
                </Tooltip>
            )
        },
        {
            field: 'playerName',
            headerName: 'Name',
            flex: 1.2,
            minWidth: 150,
            maxWidth: 250,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'roleName',
            headerName: 'Role',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1).toLowerCase() : '',
        },
        {
            field: 'playerStatus',
            headerName: 'Status',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const val = params.value;
                return (
                    <Chip
                        icon={val === 'ACTIVE' ? <FaCheckCircle /> : <GrInProgress />}
                        label={val === 'ACTIVE' ? 'Active' : 'Pending'}
                        color={val === 'ACTIVE' ? 'success' : 'warning'}
                        size="small"
                    />
                );
            }
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.2,
            minWidth: 180,
            maxWidth: 300,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value || 'N/A',
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || 'N/A'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'subStatus',
            headerName: 'Plan Status',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const val = params.value;
                if (!val) return <span>No plan</span>;
                
                let icon = null;
                let label = val;
                let color = 'default';
                
                if (val === 'active') {
                    icon = <FaCheckCircle style={{ color: '#2e7d32', marginRight: '4px' }} />;
                    label = 'Active';
                    color = 'success';
                } else if (val === 'paused') {
                    icon = <MdOutlineMotionPhotosPaused style={{ color: '#ed6c02', marginRight: '4px' }} />;
                    label = 'Paused';
                    color = 'warning';
                } else if (val === 'trialing') {
                    icon = <img src={freeTierImg} style={{ height: '20px', marginRight: '4px' }} alt="free" />;
                    label = 'Free Tier';
                    color = 'info';
                } else if (val === 'canceled') {
                    icon = <IoIosCloseCircle style={{ color: '#d32f2f', marginRight: '4px' }} />;
                    label = 'Canceled';
                    color = 'error';
                }
                
                return (
                    <Chip
                        icon={icon}
                        label={label}
                        color={color}
                        size="small"
                    />
                );
            }
        },
        {
            field: 'planName',
            headerName: 'Plan Name',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => {
                if (!params?.value) return 'No plan';
                return params.value.replaceAll('-', ' ');
            }
        },
        {
            field: 'actions',
            headerName: 'Cancel Plan?',
            flex: 0.6,
            minWidth: 120,
            maxWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                const canCancel = privileges?.MANAGE_CANCEL_USER_PLAN?.includes(Privileges.CANCEL);
                const showCancel = row.subStatus === 'active' && row.planName !== 'pay-as-you-go';
                
                if (!showCancel || !canCancel) return null;
                
                return (
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => {
                            const userName = row.playerName || '';
                            cancelSubscription(row.subId, row.playerId, userName);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                );
            }
        },
    ], [privileges]);

    const rowsWithId = users.map((row, index) => ({
        ...row,
        id: row.playerId || row.id || `row-${index}`
    }));

    // Check if any loading state is true
    const isLoading = loadingState.loading1 || loadingState.loading2 || loadingState.loading3;

    return (
        <div className="member-list-container">
            <div className="add-member-heading">
                <h1>Manage {getPlayerLabelByGame(selectedClub?.games && selectedClub?.games[0]?.gameName)}</h1>
            </div>
            <div className="member-list-table">
                <div style={{ height: 850, width: '100%', background: 'white', borderRadius: '8px', padding: '16px' }}>
                    <DataGrid
                        rows={rowsWithId}
                        columns={columns}
                        pagination
                        paginationMode="server"
                        rowCount={users?.length !== 0 ? users[0]?.totalPlayer : 0}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(newModel) => {
                            setPaginationModel(newModel);
                        }}
                        pageSizeOptions={[50, 100]}
                        loading={isLoading}
                        disableSelectionOnClick
                        getRowId={(row) => row.id}
                        
                        // ✅ Sorting
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        
                        // ✅ Filter handling
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterChange}
                        
                        // ✅ Ensure column resizing is enabled
                        disableColumnResize={false}
                        
                        slots={{
                            toolbar: () => (
                                <CustomToolbar
                                    quickFilterProps={{
                                        debounceMs: 400,
                                    }}
                                />
                            ),
                        }}
                        hideFooterPagination={false}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f5f5f5',
                                borderBottom: '2px solid #e8e8e8',
                                fontWeight: 600,
                                color: '#333',
                                fontSize: '14px',
                                textTransform: 'uppercase',
                            },
                            // ✅ Clean column separator - let MUI handle resizing
                            '& .MuiDataGrid-columnSeparator': {
                                visibility: 'visible',
                                color: '#d1d1d1',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                                padding: '8px 16px',
                                fontSize: '14px',
                                color: '#333',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#f5f9ff',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid #e8e8e8',
                                padding: '8px 16px',
                                backgroundColor: '#fafafa',
                            },
                            '& .MuiDataGrid-toolbarContainer': {
                                padding: 0,
                            },
                            border: 'none',
                            borderRadius: '8px',
                            // ✅ Hide column menu icon
                            '& .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader:focus': {
                                outline: 'none',
                            },
                        }}
                    />
                </div>
            </div>
            <CustomDialog
                isOpen={openDeleteModal}
                submitBtnName="Yes"
                onSubmit={cancelSubscriptionApi}
                onCancel={() => setOpenDeleteModal(false)}
                onClose={() => setOpenDeleteModal(false)}
            >
                <FormControlLabel
                    control={<Checkbox
                        onChange={(e) => setIsRefundAll(e.target.checked)} className="reservation-calcel-checkbox"
                    />}
                    label="Refund all reserved reservation payment"
                />
            </CustomDialog>
        </div>
    )
}

export default PlayerList